import * as amplitude from '@amplitude/analytics-browser';
import { isOkToLoad } from '../utils/isOkToLoad';

/*
 * Amplitude Browser SDK 2. autocapture.attribution handles referrer/gclid/session
 * attribution, but its isNewCampaign() gate skips capture whenever the referrer is
 * in excludeReferrers (our own marketing domains) — which silently drops the very
 * campaign params that arrive on the paid ad -> marketing page -> app handoff. The
 * legacy SDK had no such gate, so to preserve that behavior we read these params
 * straight from the URL and set them as user properties ourselves, each as
 * last-touch (set) + first-touch (setOnce initial_*):
 *   - STANDARD_UTM_KEYS: legacy includeUtm:true
 *   - CLICK_ID_KEYS:     legacy includeGclid:true / includeFbclid:true
 *   - CUSTOM_UTM_KEYS:   Google Ads params the SDK never captures at all
 * referrer/referring_domain stay with autocapture — they come from document.referrer
 * (not the URL), and we intentionally want the referrer gate to suppress internal nav.
 */
const STANDARD_UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
];

const CLICK_ID_KEYS = [
    'gclid',
    'fbclid'
];

const CUSTOM_UTM_KEYS = [
    'utm_realsearchphrase',
    'utm_adid',
    'utm_placement',
    'utm_network',
    'utm_matchtype'
];

function setAttributionUserProperties() {
    const params = new URLSearchParams(window.location.search);
    const captured: { [key: string]: string } = {};
    [...STANDARD_UTM_KEYS, ...CLICK_ID_KEYS, ...CUSTOM_UTM_KEYS].forEach(key => {
        const value = params.get(key);
        if (value) captured[key] = value;
    });
    if (Object.keys(captured).length === 0) {
        return;
    }
    const identify = new amplitude.Identify();
    Object.entries(captured).forEach(([key, value]) => {
        identify.setOnce('initial_' + key, value);
        identify.set(key, value);
    });
    amplitude.identify(identify);
}

if (typeof window !== 'undefined' && isOkToLoad && process.env.NEXT_PUBLIC_AMPLITUDE_ID) {
    amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_ID, undefined, {
        // Our script is the single source of truth; don't let dashboard remote config override it.
        fetchRemoteConfig: false,
        autocapture: {
            attribution: {
                // userProperty: broad first/last-touch attribution. eventProperty: multi-touch in Data Tables.
                trackingMethod: ['userProperty', 'eventProperty'],
                resetSessionOnNewCampaign: false,
                // Fires an [Amplitude] Attribution heartbeat on every page load — critical for bounce tracking.
                fallbackAttributionEvent: true,
                // $ anchor covers all subdomains so navigation between our own domains doesn't reset attribution to "direct".
                excludeReferrers: [
                    /sunnyside\.co$/,
                    /joinsunnysidemed\.com$/,
                    /unbounce\.com$/,
                    /unbouncepages\.com$/
                ]
            },
            sessions: true,
            pageViews: true,
            formInteractions: true,
            fileDownloads: false,
            elementInteractions: false
        },
        // Legacy SDK may have generated device IDs shorter than the new SDK's 5-char default; prevents identity breaks.
        minIdLength: 1,
        cookieOptions: {
            // Read legacy AMP_dad6fa cookies and migrate them to the new AMP_dad6fae978 format to preserve identity.
            upgrade: true
        }
    });
    setAttributionUserProperties();
}

export default amplitude;
